import { createElement, useEffect, useState, Suspense } from 'react';
import { DisplayDialogProps, Platform, ShortcutsConfig, getKeyLabel } from '..';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { Localizer } from '../../../locale/useTextLocalizer';
import { getOSName } from '../../../util';
import { Button, ButtonVariant } from '../../Button';
import { Dialog } from '../../Dialog';
import { Flow } from '../../Layouts/Flow';
import { Label } from '../../Label';
import { useListState } from '../../List/useListState';
import { Item, List, GroupHeaderItem } from '../../List';
import { SearchField } from '../../SearchField';
import { useParentSize } from '../../../hooks/useParentSize';
import bundle from '../NovaComponents-KeyboardShortcuts-gui-icu.properties';

import cn from './DisplayDialog.css.json';
import './DisplayDialog.css';

const useTextLocalizer = createUseTextLocalizer(bundle);

export const DisplayDialog = (props: DisplayDialogProps) => {
  const localize = useTextLocalizer();

  const listState = useListState();

  const [searchValue, setSearchValue] = useState('');

  const { listData, matchCount } = getListData(props.shortcuts, localize, searchValue);

  const [setRef, dimensions] = useParentSize();

  useEffect(() => {
    // clear search value after close
    if (!props.isOpen) setSearchValue('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  return (
    <Dialog
      isOpen={props.isOpen}
      testId={props.testId}
      title={localize('displayDialog.dialog.title')}
      resizable
      size={{
        width: '60rem',
        height: '48rem',
        minHeight: '20rem',
      }}
      dismissAction={{
        text: localize('close.button.label'),
        onPress: props.onClose,
      }}
      subContentFooterControl={
        <Suspense fallback={null}>
          <Label
            helpPopup={{ contentKey: 'keyboard.shortcutKeysNote', library: 'novacmneua-gui-eua' }}
          >
            {localize('footNote.msg')}
          </Label>
        </Suspense>
      }
      footerControls={
        props.hideLinkToManage ? undefined : (
          <Button
            className={cn.linkButton}
            text={localize('linkToManage.label')}
            variant={ButtonVariant.Light}
            onPress={props.onLinkPress}
          />
        )
      }
    >
      <Flow className={cn.content} orientation="vertical">
        <SearchField
          className={cn.searchField}
          value={searchValue}
          onChange={(e) => {
            listState.clearMeasurementCache({ startRowIndex: 0, startColumnIndex: 0 });
            setSearchValue(e.value);
          }}
          filterMode
          numberOfResults={matchCount}
        />
        <div className={cn.listContainer}>
          <div ref={setRef} className={cn.listParent}>
            <List
              {...listState}
              ariaLabel={localize('actionsAndShortcuts.aria')}
              rowCount={listData.length}
              height={dimensions?.height}
              width={dimensions?.width}
              showSeparator={() => true}
              renderItem={({ index }) => {
                const item = listData[index];
                if ('group' in item) return <GroupHeaderItem title={item.group} />;
                return (
                  <Item
                    highlight={{ text: searchValue }}
                    title={item.title}
                    info={{ infoText: item.info }}
                  />
                );
              }}
            />
          </div>
        </div>
      </Flow>
    </Dialog>
  );
};

function getListData(shortcuts: ShortcutsConfig, localize: Localizer, searchValue: string) {
  const platform = getOSName().toLowerCase() as Platform;
  const listData: (
    | {
        group: string;
      }
    | {
        title: string;
        info: string;
      }
  )[] = [];
  const lowercaseValue = searchValue.toLowerCase();
  let matchCount = 0;
  for (const i in shortcuts) {
    if (shortcuts.hasOwnProperty(i)) {
      const group = shortcuts[i];
      if (group.description.endsWith('.label')) group.description = localize(group.description);
      listData.push({
        group: group.description,
      });
      const prevCount = matchCount;

      const actionList = group.actions;
      for (const j in actionList) {
        if (actionList.hasOwnProperty(j)) {
          const action = actionList[j];
          const defaults = action.default[platform];
          let keys = action[platform];
          let keysInfo = '';
          if (action.description.endsWith('.label'))
            action.description = localize(action.description);
          if (!keys && defaults) {
            keys = defaults;
          }
          if (keys) {
            keysInfo = getKeyLabel(keys[0] || keys[1], localize);
            if (keys[0] && keys[1]) {
              keysInfo += `\n${getKeyLabel(keys[1], localize)}`;
            }
          }
          if (
            keysInfo &&
            (!searchValue ||
              action.description.toLowerCase().includes(lowercaseValue) ||
              keysInfo.toLowerCase().includes(lowercaseValue))
          ) {
            matchCount++;
            listData.push({
              title: action.description,
              info: keysInfo,
            });
          }
        }
      }
      if (prevCount === matchCount) {
        // no matching item, remove the group header
        listData.pop();
      }
    }
  }
  return { listData, matchCount };
}
